import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createRepo } from 'lib/api/firebase';
import moment from 'moment';

import { Collection, Inventory, PatientSubcollection } from 'lib/documents';
import { handleError } from 'lib/utils/redux';
import {
  PendingAssessmentData,
  QuestionnaireControlFlowState,
  QuestionnaireTestNames
} from './questionnaire-control-flow-slice-types';
import { assessmentSubmitInventory } from 'lib/api/firebase/apis/apis';
import { omit } from 'lodash';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { AssessmentAPI } from 'lib/api/firebase/apis/api-assessment';

/**
 * Fetch questionnaire data from the database.
 * This can be used to fetch data outside of asynchronous state management
 */
export async function _loadData(
  patientId: string,
  rootPath: Collection.Patients | Collection.PendingPatientAssessments
) {
  const pendingAssessmentDataRepo = createRepo<PendingAssessmentData>([
    Collection.PendingPatientAssessments
  ]);
  const pendingAssessmentData =
    (await pendingAssessmentDataRepo.find(patientId)) ?? null;

  const collectionPaths = [
    [rootPath, patientId, PatientSubcollection.InventoriesPromis],
    [rootPath, patientId, PatientSubcollection.InventoriesCgs],
    [rootPath, patientId, PatientSubcollection.InventoriesPcs],
    [rootPath, patientId, PatientSubcollection.InventoriesDhi],
    [rootPath, patientId, PatientSubcollection.InventoriesImpact]
  ];

  const repos = collectionPaths.map(path => createRepo<Inventory>(path));
  const promises = repos.map(repo => repo.first({ orderBy: ['date', 'desc'] }));

  return Promise.all(promises).then(results => {
    const returnResults: Record<
      (typeof QuestionnaireTestNames)[number],
      Inventory
    > = {} as any;

    // Transform the results list into an object
    results.forEach((res, index) => {
      const missingData =
        !res ||
        // undefined result means there are no records according ot our createRepo function!
        res === undefined ||
        moment().diff(res?.date, 'days') > 30 ||
        everyDataHasValue(res) === false;

      if (missingData) {
        returnResults[QuestionnaireTestNames[index]] = {
          id: 'unknown',
          data: {},
          date: null as any
        };
        return;
      }

      returnResults[QuestionnaireTestNames[index]] = res;
    });

    return { returnResults, pendingAssessmentData };
  });
}

export const questionnaireInfoInitialLoad = createAsyncThunk<
  Awaited<ReturnType<typeof _loadData> | null>,
  {
    patientId: string;
    rootPath: Parameters<typeof _loadData>[1];
  }
>('questionnaireControl/initialLoad', async data => {
  if (data.patientId === null) return null;
  return await _loadData(data.patientId, data.rootPath);
});

export const startAssessment = createAsyncThunk(
  'questionnaireControl/init',
  (assessmentId: string, { dispatch }) => {
    dispatch(questionnaireControlSlice.actions.startLoading());
    return _initAssessmentData({ patientId: assessmentId });
  }
);

export async function _initAssessmentData(
  args: Parameters<typeof AssessmentAPI.start>[0]
) {
  const result = await AssessmentAPI.start(args);
  return result.data as PendingAssessmentData;
}

/**
 * Save an inventory to the pending assessment collection
 *
 * @param patientId
 * @param subcollection
 * @param data
 */
export async function savePendingInventory(
  subcollection: PatientSubcollection,
  data: Inventory
) {
  try {
    await assessmentSubmitInventory(subcollection, omit(data, ['date']));
  } catch (e) {
    /**
     * Improve the error message
     * TODO: Use custom error classes
     */
    const _error = resolveError(e);
    const error = new Error(
      `[savePendingAssessment] Failed to save inventory ${subcollection}. Reason: ${_error.message}`
    );
    error.stack = _error.stack;
    error.cause = _error.cause;
    throw error;
  }
}

export const initialState: QuestionnaireControlFlowState = {
  required: 0,
  fulfilled: 0,
  data: {},
  order: QuestionnaireTestNames as any,
  done: {},
  allDone: true,
  isLoading: true,
  hasError: false,
  error: null,
  assessmentData: null
};

export const questionnaireControlSlice = createSlice({
  name: 'questionnaireControl',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    stopLoading(state) {
      state.isLoading = false;
    }
  },
  extraReducers: builder => {
    // builder.addCase(initialLoad.pending, handlePending);
    builder.addCase(questionnaireInfoInitialLoad.rejected, (...args) =>
      handleError(...args, '[QuestionnaireControlFlowSlice][initialLoad]')
    );
    builder.addCase(
      questionnaireInfoInitialLoad.fulfilled,
      (state, { payload }) => {
        if (payload === null) return;

        state.data = payload.returnResults;
        const { newDone, required, fulfilled } = createDoneMap(
          payload.returnResults
        );
        state.assessmentData = payload.pendingAssessmentData;

        state.done = newDone;
        state.required = required;
        state.fulfilled = fulfilled;
        state.allDone = required > 0 && fulfilled == required;
        state.isLoading = false;
      }
    );

    builder.addCase(startAssessment.rejected, (...args) =>
      handleError(...args, '[QuestionnaireControlSlice][initAssessmentData]')
    );
    builder.addCase(startAssessment.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.assessmentData = payload;
    });
  }
});

/**
 * Second export to start migrating code
 */
export const QuestionnaireControlSliceReducer =
  questionnaireControlSlice.reducer;

export function createDoneMap(payload: Record<string, Inventory>) {
  const newDone: Record<string, boolean> = {};
  const keys = Object.keys(payload);
  let fulfilled = 0;
  const required = keys.length;

  keys.forEach(testName => {
    const done = payload[testName]?.date !== null;
    if (done) {
      fulfilled++;
    }

    newDone[testName] = done;
  });
  return { newDone, required, fulfilled };
}

const NO_VALUE = -1;

function everyDataHasValue(res: Inventory) {
  for (const [, value] of Object.entries(res.data ?? {})) {
    if (value === NO_VALUE) return false;
  }
  return true;
}
